import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import AdminLayout from '../../components/feature/AdminLayout';
import {
  fetchAdminProducts,
  createAdminProduct,
  updateAdminProduct,
  deleteAdminProduct,
  type PlatformProduct,
  type ProductType,
} from '../../api/releases.api';

const PRODUCT_TYPE_LABELS: Record<ProductType, { label: string; badge: string; icon: string }> = {
  cloud: { label: 'سحابي (Cloud SaaS)', badge: 'bg-blue-50 text-blue-700 border-blue-200', icon: 'ri-cloud-line' },
  desktop: { label: 'مكتبي (Desktop App)', badge: 'bg-purple-50 text-purple-700 border-purple-200', icon: 'ri-macbook-line' },
  mobile: { label: 'تطبيق هاتف (Mobile)', badge: 'bg-emerald-50 text-emerald-700 border-emerald-200', icon: 'ri-smartphone-line' },
  web: { label: 'ويب مستقل (Web App)', badge: 'bg-amber-50 text-amber-700 border-amber-200', icon: 'ri-global-line' },
};

export default function ProductsListPage() {
  const [products, setProducts] = useState<PlatformProduct[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  // Modal State
  const [modalOpen, setModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<PlatformProduct | null>(null);
  const [saving, setSaving] = useState(false);

  // Form State
  const [name, setName] = useState('');
  const [nameAr, setNameAr] = useState('');
  const [slug, setSlug] = useState('');
  const [type, setType] = useState<ProductType>('desktop');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [docsUrl, setDocsUrl] = useState('');

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError('');
    const res = await fetchAdminProducts();
    setLoading(false);

    if (!res.ok) {
      setError(res.message);
      return;
    }
    setProducts(res.products);
  }, []);

  useEffect(() => {
    void loadProducts();
  }, [loadProducts]);

  const openCreateModal = () => {
    setEditingProduct(null);
    setName('');
    setNameAr('');
    setSlug('');
    setType('desktop');
    setDescription('');
    setIsActive(true);
    setWebsiteUrl('');
    setDocsUrl('');
    setModalOpen(true);
  };

  const openEditModal = (p: PlatformProduct) => {
    setEditingProduct(p);
    setName(p.name);
    setNameAr(p.name_ar ?? '');
    setSlug(p.slug);
    setType(p.type);
    setDescription(p.description ?? '');
    setIsActive(Boolean(p.is_active));
    setWebsiteUrl(p.website_url ?? '');
    setDocsUrl(p.docs_url ?? '');
    setModalOpen(true);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;

    setSaving(true);
    setError('');
    setSuccessMsg('');

    const payload = {
      name: name.trim(),
      name_ar: nameAr.trim() || null,
      slug: slug.trim() || undefined,
      type,
      description: description.trim() || null,
      is_active: isActive,
      website_url: websiteUrl.trim() || null,
      docs_url: docsUrl.trim() || null,
    };

    if (editingProduct) {
      const res = await updateAdminProduct(editingProduct.id, payload);
      setSaving(false);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccessMsg(`تم تحديث بيانات المنتج "${name}" بنجاح.`);
    } else {
      const res = await createAdminProduct(payload);
      setSaving(false);
      if (!res.ok) {
        setError(res.message);
        return;
      }
      setSuccessMsg(`تمت إضافة المنتج الجديد "${name}" بنجاح.`);
    }

    setModalOpen(false);
    void loadProducts();
  };

  const handleDelete = async (p: PlatformProduct) => {
    if (!window.confirm(`هل أنت متأكد من حذف المنتج "${p.name}"؟`)) return;
    setError('');
    setSuccessMsg('');

    const res = await deleteAdminProduct(p.id);
    if (!res.ok) {
      setError(res.message);
      return;
    }

    setSuccessMsg(`تم حذف المنتج "${p.name}".`);
    void loadProducts();
  };

  return (
    <AdminLayout>
      <div className="space-y-6 max-w-5xl mx-auto pb-12" dir="rtl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
          <div>
            <div className="flex items-center gap-2 text-xs text-gray-500 mb-1">
              <Link to="/releases" className="hover:text-blue-600 transition-colors">
                إدارة الإصدارات
              </Link>
              <i className="ri-arrow-left-s-line" />
              <span>إدارة المنتجات البرمجية</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">
              كتالوج منتجات DressnMore البرمجية
            </h1>
            <p className="text-xs text-gray-500 mt-0.5">
              إدارة المنتجات المستقلة (الأنظمة السحابية، نسخ الديسكتوب، والتطبيقات المستقبلية)
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Link
              to="/releases"
              className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 hover:bg-gray-50 text-sm font-medium"
            >
              العودة لقائمة الإصدارات
            </Link>

            <button
              onClick={openCreateModal}
              className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-bold shadow-sm shadow-blue-200 flex items-center gap-1.5"
            >
              <i className="ri-add-line text-lg" />
              إضافة منتج جديد
            </button>
          </div>
        </div>

        {/* Notifications */}
        {error && (
          <div className="p-4 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl flex items-center gap-3">
            <i className="ri-error-warning-line text-xl" />
            <span>{error}</span>
          </div>
        )}

        {successMsg && (
          <div className="p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl flex items-center gap-3">
            <i className="ri-checkbox-circle-line text-xl" />
            <span>{successMsg}</span>
          </div>
        )}

        {/* Products Grid */}
        {loading ? (
          <div className="bg-white p-12 rounded-2xl border border-gray-100 text-center text-gray-500">
            <i className="ri-loader-4-line text-3xl animate-spin text-blue-600 mb-2 inline-block" />
            <p>جاري تحميل قائمة المنتجات...</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {products.map((p) => {
              const typeMeta = PRODUCT_TYPE_LABELS[p.type] || PRODUCT_TYPE_LABELS.cloud;
              const currentRel = p.current_release;

              return (
                <div
                  key={p.id}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 flex flex-col justify-between hover:border-gray-200 transition-all space-y-4"
                >
                  <div className="space-y-3">
                    <div className="flex items-start justify-between gap-2">
                      <div className="w-12 h-12 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center text-2xl">
                        <i className={typeMeta.icon} />
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-md border ${typeMeta.badge}`}>
                          {typeMeta.label}
                        </span>
                        {p.is_active ? (
                          <span className="w-2.5 h-2.5 rounded-full bg-emerald-500" title="نشط" />
                        ) : (
                          <span className="w-2.5 h-2.5 rounded-full bg-gray-300" title="غير نشط" />
                        )}
                      </div>
                    </div>

                    <div>
                      <h3 className="font-bold text-gray-900 text-lg">{p.name}</h3>
                      <div className="text-xs font-mono text-gray-400 mt-0.5">slug: {p.slug}</div>
                    </div>

                    {p.description && (
                      <p className="text-xs text-gray-600 line-clamp-2 leading-relaxed">
                        {p.description}
                      </p>
                    )}

                    {/* Current release info */}
                    <div className="p-3 rounded-xl bg-gray-50 border border-gray-100 space-y-1">
                      <div className="text-[11px] font-semibold text-gray-500">الإصدار المعتمد الحالي:</div>
                      {currentRel ? (
                        <div className="flex items-center justify-between">
                          <span className="font-mono font-bold text-gray-900 text-sm">
                            v{currentRel.version}
                          </span>
                          <span className="text-[11px] text-gray-500">
                            {currentRel.release_date}
                          </span>
                        </div>
                      ) : (
                        <div className="text-xs text-gray-400 italic">لا يوجد إصدار معتمد حالياً</div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="pt-3 border-t border-gray-100 flex items-center justify-between">
                    <Link
                      to={`/releases?product_id=${p.id}`}
                      className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
                    >
                      <i className="ri-git-branch-line" />
                      عرض الإصدارات ({p.releases_count ?? 0})
                    </Link>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => openEditModal(p)}
                        className="w-7 h-7 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 flex items-center justify-center transition-colors"
                        title="تعديل المنتج"
                      >
                        <i className="ri-edit-line text-sm" />
                      </button>

                      <button
                        onClick={() => void handleDelete(p)}
                        className="w-7 h-7 rounded-lg text-gray-400 hover:text-rose-600 hover:bg-rose-50 flex items-center justify-center transition-colors"
                        title="حذف المنتج"
                      >
                        <i className="ri-delete-bin-line text-sm" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Create / Edit Modal */}
        {modalOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
            <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl space-y-4" dir="rtl">
              <div className="flex items-center justify-between border-b border-gray-100 pb-3">
                <h3 className="font-bold text-gray-900 text-base">
                  {editingProduct ? 'تعديل بيانات المنتج' : 'إضافة منتج برمجي جديد'}
                </h3>
                <button
                  onClick={() => setModalOpen(false)}
                  className="w-8 h-8 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 flex items-center justify-center"
                >
                  <i className="ri-close-line text-lg" />
                </button>
              </div>

              <form onSubmit={(e) => void handleSave(e)} className="space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">اسم المنتج *</label>
                  <input
                    type="text"
                    required
                    placeholder="مثال: DressnMore POS"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">الاسم بالعربية (اختياري)</label>
                  <input
                    type="text"
                    placeholder="مثال: دريس أند مور للمحاسبة السريعة"
                    value={nameAr}
                    onChange={(e) => setNameAr(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">المعرف البرمجي (Slug)</label>
                    <input
                      type="text"
                      placeholder="dressnmore-pos"
                      value={slug}
                      onChange={(e) => setSlug(e.target.value)}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm font-mono focus:outline-none focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 mb-1">نوع المنتج *</label>
                    <select
                      value={type}
                      onChange={(e) => setType(e.target.value as ProductType)}
                      className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500 bg-white"
                    >
                      <option value="desktop">مكتبي (Desktop App)</option>
                      <option value="cloud">سحابي (Cloud SaaS)</option>
                      <option value="mobile">تطبيق هاتف (Mobile)</option>
                      <option value="web">ويب مستقل (Web)</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">الوصف</label>
                  <textarea
                    rows={2}
                    placeholder="وصف مختصر عن وظيفة هذا المنتج والجمهور المستهدف..."
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-blue-500"
                  />
                </div>

                <div className="flex items-center gap-2 pt-1">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="is_active" className="text-xs font-semibold text-gray-700 cursor-pointer">
                    المنتج نشط ومتاح في قائمة المنتجات
                  </label>
                </div>

                <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setModalOpen(false)}
                    className="px-4 py-2 rounded-xl border border-gray-200 text-gray-700 text-xs font-semibold"
                  >
                    إلغاء
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="px-5 py-2 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold shadow-sm"
                  >
                    {saving ? 'جاري الحفظ...' : 'حفظ المنتج'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
